import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch } from '@/shared/utils/get-url-params';
import { handleAxiosError } from '@/shared/utils/general/axios.utils';
import { urlBackend } from '@/shared/constants/networking.constants';
import { erpApi } from '@/shared/axios/erp-api';
import {
  AlertSeverityEnum,
} from '@/shared/interfaces/erp/global/errors.interface';
import {
  Parametro,
} from '@/shared/interfaces/erp/administration/system-parameters/parametro.interface';
import {
  ParametrosPagingRes,
} from '@/shared/interfaces/erp/administration/system-parameters/parametro.interface';
import {
  UseFetchEnabledParams,UseMutationsParams
} from '@/shared/interfaces/erp/global/global.interface';
import { useUiStore } from '@/store/ui/ui.store';

const { get, post, put } = erpApi();

export enum ParametroQueryKeyEnum {
  PARAMETROS = 'parametros',
  PARAMETRO = 'parametro',
}

///* tanStack query
export const useFetchParametros = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetParametrosParams> = {}) => {
  return useQuery({
    queryKey: [
      ParametroQueryKeyEnum.PARAMETROS,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getParametros(params),
    enabled: enabled,
  });
};

///* Catálogo de solo lectura (técnico): misma forma que `useFetchParametros`, sin
///* exigir `api.view_parametro`. Solo slugs permitidos por el backend
///* (`parametro-catalogo`). Comparte el prefijo de la clave con el listado para que
///* la edición del catálogo en Administración lo invalide.
export const useFetchParametroCatalogo = (slug: string, enabled = true) => {
  return useQuery({
    queryKey: [ParametroQueryKeyEnum.PARAMETROS, 'catalogo', slug],
    queryFn: () => getParametroCatalogo(slug),
    enabled: enabled && !!slug,
  });
};

export const useGetParametro = (id: string) => {
  return useQuery({
    queryKey: [ParametroQueryKeyEnum.PARAMETRO, id],
    queryFn: () => getParametro(id),
    retry: false,
  });
};

export const useCreateParametro = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateParametroParams<T>) => createParametro(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ParametroQueryKeyEnum.PARAMETROS],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Parámetro creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateParametro = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateParametroParams<T>) => updateParametro(params),
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [ParametroQueryKeyEnum.PARAMETROS],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Parámetro actualizado correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetParametrosParams = Partial<Parametro> & {
  page?: number;
  page_size?: number;
  filterByState?: boolean;
};

export type CreateParametroParamsForm = Omit<Parametro, 'id_parametro'>;
export type CreateParametroParams<T> = T;
export type UpdateParametroParams<T> = {
  id: string;
  data: T;
};

export const getParametros = async (params?: GetParametrosParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<ParametrosPagingRes>(
    `${urlBackend}api/v1/parametro/?${queryParams}`,
    true
  );
};

export const getParametroCatalogo = async (slug: string) => {
  return get<ParametrosPagingRes>(
    `${urlBackend}api/v1/parametro-catalogo/?slug=${encodeURIComponent(slug)}`,
    true
  );
};

export const getParametro = async (id: string) => {
  try {
    return await get<Parametro>(`${urlBackend}api/v1/parametro/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createParametro = async <T>(data: CreateParametroParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Parametro>(`${urlBackend}api/v1/parametro/`, data, true);
};

export const updateParametro = async <T>({
  id,
  data,
}: UpdateParametroParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Parametro>(`${urlBackend}api/v1/parametro/${id}/`, data, true);
};
